import {
  type NewsDeliveryOption,
  type NewsJobMode,
  type NewsNotificationType,
  type NewsReadingProfile,
} from "./api";
import {
  API_BASE_URL,
  NEWS_GOOGLE_RECONSENT_COMPLETE_PATH,
  resolveNewsGoogleReconsentCompleteUrl,
} from "./config";
import { GOOGLE_BUNDLE_BASE_SCOPE_BUNDLE } from "./googleConnectResolution";

export const NEWS_GOOGLE_RECONSENT_STORAGE_KEY = "wizmate.news.google-reconsent.v1";
export const NEWS_GOOGLE_RECONSENT_TTL_MS = 30 * 60 * 1000;

const TRUSTED_GOOGLE_AUTH_HOSTS = new Set(["accounts.google.com"]);
const TRUSTED_NEWS_CONNECT_PATHS = new Set([
  "/api/v1/news/jobs/connect",
  "/integrations/google/connect",
]);
const NEWS_CONNECT_TOKEN_PATH_REGEX = /^\/api\/v1\/news\/jobs\/[A-Za-z0-9_-]{16,}$/;

export type PendingNewsGoogleReconsentSnapshot = {
  keyword: string;
  notificationType: NewsNotificationType;
  email: string;
  wizmateUserId: string;
  scopeBundle: string;
  authorizationUrl: string;
  redirectUrl: string;
  createdAt: string;
  mode?: NewsJobMode;
  readingProfile?: NewsReadingProfile;
  deliveryOption?: NewsDeliveryOption;
};

function normalize(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed || undefined;
}

function hasWindow() {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function normalizeOriginByUrl(value: string): string | undefined {
  try {
    return new URL(value).origin;
  } catch {
    return undefined;
  }
}

function isLocalDevHostname(hostname: string): boolean {
  const normalized = hostname.toLowerCase();
  return normalized === "localhost" || normalized === "127.0.0.1" || normalized === "::1" || normalized === "[::1]";
}

function parseAsAbsoluteUrl(value: string): URL | null {
  try {
    if (typeof window !== "undefined" && window.location?.origin) {
      return new URL(value, window.location.origin);
    }

    return new URL(value);
  } catch {
    return null;
  }
}

function normalizePathname(pathname: string): string {
  if (!pathname) return "/";
  const normalized = pathname.replace(/\/+$/, "");
  return normalized || "/";
}

function isTrustedNewsConnectPath(pathname: string): boolean {
  const normalizedPathname = normalizePathname(pathname);

  if (TRUSTED_NEWS_CONNECT_PATHS.has(normalizedPathname)) {
    return true;
  }

  return NEWS_CONNECT_TOKEN_PATH_REGEX.test(normalizedPathname);
}

export function normalizeNewsGoogleAuthorizationUrl(value: unknown): string | undefined {
  const raw = normalize(value);
  if (!raw) return undefined;

  const parsed = parseAsAbsoluteUrl(raw);
  if (!parsed) return undefined;

  const protocol = parsed.protocol.toLowerCase();
  if (protocol !== "https:" && !(protocol === "http:" && isLocalDevHostname(parsed.hostname))) {
    return undefined;
  }

  const trustedOrigins = new Set<string>();
  if (typeof window !== "undefined" && window.location?.origin) {
    trustedOrigins.add(window.location.origin);
  }

  const apiOrigin = normalizeOriginByUrl(API_BASE_URL);
  if (apiOrigin) {
    trustedOrigins.add(apiOrigin);
  }

  const isTrustedGoogle = TRUSTED_GOOGLE_AUTH_HOSTS.has(parsed.hostname.toLowerCase());
  const isTrustedOrigin = trustedOrigins.has(parsed.origin);

  if (!isTrustedGoogle && !isTrustedOrigin) {
    return undefined;
  }

  if (isTrustedOrigin && !isTrustedNewsConnectPath(parsed.pathname)) {
    return undefined;
  }

  return parsed.toString();
}

export function normalizeNewsGoogleReconsentRedirectUrl(value: unknown): string | undefined {
  const raw = normalize(value);
  if (!raw) return undefined;

  const parsed = parseAsAbsoluteUrl(raw);
  if (!parsed) return undefined;

  const protocol = parsed.protocol.toLowerCase();
  if (protocol !== "https:" && !(protocol === "http:" && isLocalDevHostname(parsed.hostname))) {
    return undefined;
  }

  if (typeof window !== "undefined" && window.location?.origin) {
    if (parsed.origin !== window.location.origin) {
      return undefined;
    }
  }

  if (normalizePathname(parsed.pathname) !== NEWS_GOOGLE_RECONSENT_COMPLETE_PATH) {
    return undefined;
  }

  return resolveNewsGoogleReconsentCompleteUrl();
}

function isExpiredNewsGoogleReconsentSnapshot(createdAt: string): boolean {
  const createdAtMs = Date.parse(createdAt);
  if (!Number.isFinite(createdAtMs)) return true;

  return Date.now() - createdAtMs > NEWS_GOOGLE_RECONSENT_TTL_MS;
}

export function writePendingNewsGoogleReconsentSnapshot(snapshot: PendingNewsGoogleReconsentSnapshot) {
  if (!hasWindow()) return;
  if (snapshot.notificationType !== "email") return;

  const authorizationUrl = normalizeNewsGoogleAuthorizationUrl(snapshot.authorizationUrl);
  const redirectUrl =
    normalizeNewsGoogleReconsentRedirectUrl(snapshot.redirectUrl) ??
    resolveNewsGoogleReconsentCompleteUrl();
  const createdAt = normalize(snapshot.createdAt);

  if (!authorizationUrl || !redirectUrl || !createdAt || !Number.isFinite(Date.parse(createdAt))) {
    return;
  }

  window.localStorage.setItem(
    NEWS_GOOGLE_RECONSENT_STORAGE_KEY,
    JSON.stringify({
      keyword: snapshot.keyword,
      notificationType: snapshot.notificationType,
      email: snapshot.email,
      wizmateUserId: snapshot.wizmateUserId,
      scopeBundle: snapshot.scopeBundle,
      authorizationUrl,
      redirectUrl,
      createdAt,
      mode: snapshot.mode,
      readingProfile: snapshot.readingProfile,
      deliveryOption: snapshot.deliveryOption,
    })
  );
}

export function readPendingNewsGoogleReconsentSnapshot(): PendingNewsGoogleReconsentSnapshot | null {
  if (!hasWindow()) return null;

  const raw = window.localStorage.getItem(NEWS_GOOGLE_RECONSENT_STORAGE_KEY);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    const keyword = normalize(parsed?.keyword);
    const notificationType =
      parsed?.notificationType === "email" ? parsed.notificationType : undefined;
    const email = normalize(parsed?.email);
    const wizmateUserId = normalize(parsed?.wizmateUserId);
    // TODO: 個別スコープ再同意が安定したら news_gmail_send 既定値に戻す
    const scopeBundle = normalize(parsed?.scopeBundle) ?? GOOGLE_BUNDLE_BASE_SCOPE_BUNDLE;
    const authorizationUrl = normalizeNewsGoogleAuthorizationUrl(parsed?.authorizationUrl);
    const redirectUrl =
      normalizeNewsGoogleReconsentRedirectUrl(parsed?.redirectUrl) ??
      resolveNewsGoogleReconsentCompleteUrl();
    const createdAt = normalize(parsed?.createdAt);

    if (
      !keyword ||
      !notificationType ||
      !email ||
      !wizmateUserId ||
      !scopeBundle ||
      !authorizationUrl ||
      !redirectUrl ||
      !createdAt
    ) {
      return null;
    }

    if (isExpiredNewsGoogleReconsentSnapshot(createdAt)) {
      clearPendingNewsGoogleReconsentSnapshot();
      return null;
    }

    const mode =
      parsed?.mode === "news" || parsed?.mode === "report" ? parsed.mode : undefined;
    const readingProfile =
      parsed?.readingProfile === "grasp" ||
      parsed?.readingProfile === "organize" ||
      parsed?.readingProfile === "think"
        ? parsed.readingProfile
        : undefined;
    const deliveryOption =
      parsed?.deliveryOption === "text" ||
      parsed?.deliveryOption === "audio" ||
      parsed?.deliveryOption === "text_and_audio"
        ? parsed.deliveryOption
        : undefined;

    return {
      keyword,
      notificationType,
      email,
      wizmateUserId,
      scopeBundle,
      authorizationUrl,
      redirectUrl,
      createdAt,
      mode,
      readingProfile,
      deliveryOption,
    };
  } catch {
    return null;
  }
}

export function clearPendingNewsGoogleReconsentSnapshot() {
  if (!hasWindow()) return;
  window.localStorage.removeItem(NEWS_GOOGLE_RECONSENT_STORAGE_KEY);
}
